import { useEffect, useState } from "react";

import {
  Badge,
  InfoPanel,
  Typography,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as InstitutionIcon } from "@king-ict/eupisi-ui/components/Icon/Institution.svg";
import { getInstitutionDisplayNameAndTown } from "@king-ict/eupisi-ui/utils";
import { useTranslation } from "react-i18next";

import {
  getEvaluatedCriteria,
  getInstitutionByRequestId,
} from "../../../api/kindergartenApi";
import { useTypedSelector } from "../../../app/rootReducer";
import EvaluatedCriterion from "../../../models/Criterion/EvaluatedCriterion";
import ImportantDateEventEnum from "../../../models/ImportantDate/ImportantDateEventEnum";
import InstitutionDetails from "../../../models/Institution/InstitutionDetails";
import RequestBasicInfo from "../../../models/Request/RequestBasicInfo";
import { InstitutionDetailsModal } from "../../Institutions/InstitutionDetailsModal";
import styles from "./EvaluationResult.module.scss";

interface Props {
  requestBasicInfo: RequestBasicInfo;
}

export default function EvaluationResultPage({ requestBasicInfo }: Props) {
  const [
    institutionDetails,
    setInstitutionDetails,
  ] = useState<InstitutionDetails>({} as InstitutionDetails);

  const [evaluatedCriteria, setEvaluatedCriteria] = useState<
    EvaluatedCriterion[]
  >([] as EvaluatedCriterion[]);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const handleOpenInstitutionDetails = () => {
    if (institutionDetails) {
      setDetailsModalOpen(true);
    }
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
  };

  const pedagogicalYear = useTypedSelector((state) => state.pedagogicalYear);

  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      const criteria = await getEvaluatedCriteria(requestBasicInfo.id);
      setEvaluatedCriteria(criteria);

      const institution = await getInstitutionByRequestId(requestBasicInfo.id);
      setInstitutionDetails(institution);
    })();
  }, [requestBasicInfo.id]);

  const isDataAvailable = () => {
    return (
      requestBasicInfo.pedagogicalYearId ===
        pedagogicalYear.currentPedagogicalYear.id &&
      (pedagogicalYear.currentImportantDate.eventId ===
        ImportantDateEventEnum.RequestCreation ||
        pedagogicalYear.currentImportantDate.eventId ===
          ImportantDateEventEnum.RequestSubmission ||
        pedagogicalYear.currentImportantDate.eventId ===
          ImportantDateEventEnum.RequestEvaluation)
    );
  };

  const isDirectEnrollment = () => {
    return evaluatedCriteria.some(
      (criterion) => criterion.isProven && criterion.isDirectEnrollment
    );
  };

  const getTotalPoints = () => {
    return evaluatedCriteria
      .filter((c) => c.isProven)
      .reduce((a, b) => a + b.points, 0);
  };

  const renderEvaluatedCriteria = () => {
    return evaluatedCriteria.map((criterion) => {
      return (
        <div className={styles.selectedCriterionRow} key={criterion.id}>
          <Badge
            variant="small"
            color={criterion.isProven ? "success" : "danger"}
            value={
              criterion.isProven
                ? criterion.isDirectEnrollment
                  ? "✓ "
                  : criterion.points
                : 0
            }
          />
          <Typography
            className={styles.selectedCriterionName}
            variant="baseBold"
          >
            {criterion.criterionName}
            {criterion.isDirectEnrollment
              ? " (" + t("criteriaStep.directEnrollment") + ")"
              : null}
          </Typography>
        </div>
      );
    });
  };

  return (
    <>
      {isDataAvailable() ? (
        <InfoPanel
          color="warning"
          message={t("evaluationResult.notAvailableMessage")}
          fullWidth
        />
      ) : (
        <div className={styles.wrapper}>
          <InfoPanel
            color="success"
            message={t("evaluationResult.mainInfoMessage")}
            fullWidth
          />

          <div
            className={styles.institutionName}
            onClick={handleOpenInstitutionDetails}
          >
            <IconWrapper color="primary">
              <InstitutionIcon
                aria-label={t("accessibility.institutionIcon")}
              />
            </IconWrapper>

            <Typography variant="mediumBold" component="h3" color="primary">
              {getInstitutionDisplayNameAndTown(
                institutionDetails.name,
                institutionDetails.town
              )}
            </Typography>
          </div>

          <div className={styles.totalPoints}>
            <Badge
              value={isDirectEnrollment() ? "✓" : getTotalPoints()}
              variant="big"
              color="success"
            />
            <Typography
              className={styles.totalPointsText}
              variant="XXLargeBold"
            >
              {isDirectEnrollment()
                ? t("evaluationResult.directEnrollment")
                : t("evaluationResult.yourPoints")}
            </Typography>
          </div>
          <Typography
            className={styles.selectedCriteria}
            component="h2"
            variant="largeBold"
          >
            {t("evaluationResult.selectedCriteria")}
          </Typography>
          {renderEvaluatedCriteria()}

          <InstitutionDetailsModal
            institutionDetails={institutionDetails}
            detailsModalOpen={detailsModalOpen}
            handlerCloseDetails={handleCloseDetails}
          />
        </div>
      )}
    </>
  );
}
